import {
  UpdateAutoSumStateRequest,
  UpdateTransposeStateRequest,
} from "@/apis/preview/v2/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  updateAutoSumState,
  updateTransposeState,
} from "../../../apis/preview/v2/route";

import queries from "@/queries";
import type {
  EditableTableItemsV2,
  EditableTablePreviewResponseV2,
} from "@/apis/editable-preview/types";
import { toast } from "sonner";
import { useWorkbookIdAndTableId } from "../hook";

export const useTreeViewForFinalOutput = (
  workbookId: string | null,
  tableId: string | null,
) => {
  const {
    data: treeView,
    isLoading,
    error,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.tableTreeViewV2(),
    enabled: !!workbookId && !!tableId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  if (workbookId === null || tableId === null) {
    return { treeView: null, isLoading: true, error: null };
  }

  return { treeView, isLoading, error };
};

export const useUpdateTransposeState = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();

  const queryClient = useQueryClient();

  const { mutate: updateTransposeStateMutate, isPending } = useMutation({
    mutationFn: (data: UpdateTransposeStateRequest) => {
      if (workbookId === null || tableId === null) {
        return Promise.reject(
          new Error("Workbook ID or Table ID is not found"),
        );
      }
      return updateTransposeState(workbookId, tableId, data);
    },
    onSuccess: () => {
      const tableCtx = queries.workbook(workbookId!)._ctx.tables(tableId!);
      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.editablePreview().queryKey,
      });
    },
    onError: () => {
      toast.error("행렬 전환에 실패했습니다.", {
        duration: 3000,
      });
    },
  });

  return { updateTransposeStateMutate, isPending };
};

export const useUpdateAutoSumState = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();

  const queryClient = useQueryClient();

  const editablePreviewQueryKey = queries
    .workbook(workbookId!)
    ._ctx.tables(tableId!)
    ._ctx.editablePreview().queryKey;

  const { mutate: updateAutoSumStateMutate, isPending } = useMutation({
    mutationFn: (data: UpdateAutoSumStateRequest) => {
      if (workbookId === null || tableId === null) {
        return Promise.reject(
          new Error("Workbook ID or Table ID is not found"),
        );
      }
      return updateAutoSumState(workbookId, tableId, data);
    },
    onMutate: async ({ roleKey, useAutoSum }: UpdateAutoSumStateRequest) => {
      await queryClient.cancelQueries({ queryKey: editablePreviewQueryKey });
      const snapshots =
        queryClient.getQueriesData<EditableTablePreviewResponseV2>({
          queryKey: editablePreviewQueryKey,
        });
      const flip = (table: EditableTableItemsV2) =>
        table.roleKey === roleKey ? { ...table, useAutoSum } : table;
      const flipSection = (section: { tables: EditableTableItemsV2[] }) =>
        section?.tables
          ? { ...section, tables: section.tables.map(flip) }
          : section;
      queryClient.setQueriesData<EditableTablePreviewResponseV2>(
        { queryKey: editablePreviewQueryKey },
        (old) =>
          old
            ? {
                ...old,
                current: flipSection(old.current),
                prior: flipSection(old.prior),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) =>
        queryClient.setQueryData(key, data),
      );
      toast.error("합계 자동계산 설정 변경에 실패했습니다.", {
        duration: 3000,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: editablePreviewQueryKey,
      });
    },
  });

  return { updateAutoSumStateMutate, isPending };
};
